/**
 * PetroNexus Operator — MUI v7 theme.
 *
 * Consumes tokens from ./tokens and produces both dark + light MUI themes
 * that align with WEB_DESIGN.md. Only MUI primitives are overridden here;
 * custom Operator primitives live under components/operator/.
 */

import { createTheme, type Theme } from '@mui/material/styles';
import {
  brand,
  lightBrand,
  darkTokens,
  lightTokens,
  space,
  radii,
  fonts,
  type,
  type OperatorTokens,
} from './tokens';

// Extend MUI Theme type so consumers can read `theme.operator.*`
declare module '@mui/material/styles' {
  interface Theme {
    operator: OperatorTokens & {
      brand: typeof brand;
      space: typeof space;
      radii: typeof radii;
      fonts: typeof fonts;
      type: typeof type;
      isDark: boolean;
    };
  }
  interface ThemeOptions {
    operator?: Partial<Theme['operator']>;
  }
}

export const createOperatorTheme = (mode: 'light' | 'dark'): Theme => {
  const isDark = mode === 'dark';
  const t = isDark ? darkTokens : lightTokens;
  const b = isDark ? brand : lightBrand;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: b.goldFill,
        light: b.goldFillHover,
        dark: b.goldFillPressed,
        contrastText: '#0F0A00',
      },
      secondary: {
        main: t.textSecondary as string,
      },
      background: {
        default: t.bg,
        paper: t.card,
      },
      success: { main: t.success },
      error:   { main: t.danger },
      warning: { main: t.warning },
      info:    { main: t.info },
      text: {
        primary:   t.textPrimary,
        secondary: t.textSecondary as string,
        disabled:  t.textQuaternary as string,
      },
      divider: t.divider,
    },
    typography: {
      fontFamily: fonts.sans,
      // Base body
      body1: {
        fontSize: type.body.size,
        fontWeight: type.body.weight,
        lineHeight: type.body.line,
        letterSpacing: `${type.body.tracking}px`,
      },
      body2: {
        fontSize: type.small.size,
        fontWeight: type.small.weight,
        lineHeight: type.small.line,
        letterSpacing: `${type.small.tracking}px`,
      },
      h1: { fontSize: type.display.size,  fontWeight: type.display.weight,  lineHeight: type.display.line,  letterSpacing: `${type.display.tracking}px`  },
      h2: { fontSize: type.titleXL.size,  fontWeight: type.titleXL.weight,  lineHeight: type.titleXL.line,  letterSpacing: `${type.titleXL.tracking}px`  },
      h3: { fontSize: type.titleL.size,   fontWeight: type.titleL.weight,   lineHeight: type.titleL.line,   letterSpacing: `${type.titleL.tracking}px`   },
      h4: { fontSize: type.titleM.size,   fontWeight: type.titleM.weight,   lineHeight: type.titleM.line,   letterSpacing: `${type.titleM.tracking}px`   },
      h5: { fontSize: type.titleS.size,   fontWeight: type.titleS.weight,   lineHeight: type.titleS.line,   letterSpacing: `${type.titleS.tracking}px`   },
      h6: { fontSize: type.bodyL.size,    fontWeight: type.bodyM.weight,    lineHeight: type.bodyL.line,    letterSpacing: `${type.bodyL.tracking}px`    },
      subtitle1: { fontSize: type.bodyL.size,  fontWeight: type.bodyM.weight,  lineHeight: type.bodyL.line },
      subtitle2: { fontSize: type.body.size,   fontWeight: type.bodyM.weight,  lineHeight: type.body.line },
      button: {
        fontSize: type.smallM.size,
        fontWeight: type.smallM.weight,
        letterSpacing: '-0.1px',
        textTransform: 'none',
      },
      caption: {
        fontSize: type.small.size,
        fontWeight: type.small.weight,
        lineHeight: type.small.line,
      },
      overline: {
        fontFamily: fonts.mono,
        fontSize: type.monoLabel.size,
        fontWeight: type.monoLabel.weight,
        letterSpacing: `${type.monoLabel.tracking}px`,
        textTransform: 'uppercase',
        lineHeight: type.monoLabel.line,
      },
    },
    shape: { borderRadius: radii.r3 },
    shadows: [
      'none',
      t.shadowElev1,
      t.shadowElev2,
      t.shadowElev2,
      ...Array(21).fill(t.shadowElev2),
    ] as Theme['shadows'],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: mode,
            '--pn-canvas-bg': t.bg,
            '--pn-dashboard-canvas': t.canvasGlow,
            '--pn-surface-card': t.card,
            '--pn-surface-panel': t.panel,
            '--pn-surface-muted': t.surfaceMuted,
            '--pn-border-subtle': t.borderSubtle,
            '--pn-border-std': t.borderStd,
            '--pn-text-primary': t.textPrimary,
            '--pn-text-secondary': t.textSecondary,
            '--pn-accent-primary': b.goldFill,
            '--pn-accent-soft': t.accentSoftBg,
            '--pn-card-shadow': t.shadowCard,
            '--pn-mining-legacy-lowest': isDark ? '#0b0d1a' : t.bg,
            '--pn-mining-legacy-low': isDark ? '#191b28' : t.card,
            '--pn-mining-legacy-high': isDark ? '#272937' : t.surfaceMuted,
            '--pn-mining-legacy-highest': isDark ? '#323442' : '#E7E9E4',
            '--pn-mining-legacy-container': isDark ? '#1d1f2c' : t.card,
            '--pn-mining-legacy-primary': isDark ? brand.gold : lightBrand.gold,
            '--pn-mining-legacy-secondary': isDark ? brand.goldLight : lightBrand.goldFill,
            '--pn-mining-legacy-tertiary': isDark ? '#D9B353' : lightBrand.goldDark,
            '--pn-mining-legacy-error': isDark ? '#ffb4ab' : '#B42318',
            '--pn-mining-legacy-on': isDark ? '#e1e1f4' : t.textPrimary,
            '--pn-mining-legacy-on-muted': isDark ? '#c7c4d8' : t.textSecondary,
            '--pn-mining-legacy-outline': isDark ? '#918fa1' : t.textTertiary,
            '--pn-mining-legacy-glass-bg': isDark ? 'rgba(50,52,66,0.60)' : 'rgba(255,255,255,0.82)',
            '--pn-mining-legacy-glass-border': isDark ? 'rgba(240,184,32,0.12)' : t.borderSubtle,
            '--pn-mining-legacy-shadow': isDark ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : t.shadowCard,
            '--pn-mining-legacy-input-bg': isDark ? '#0b0d1a' : '#FFFFFF',
            '--pn-mining-map-bottom-overlay': isDark
              ? 'linear-gradient(to top, rgba(11,13,26,0.80) 0%, transparent 40%)'
              : 'linear-gradient(to top, rgba(247,248,246,0.84) 0%, rgba(247,248,246,0.20) 40%, transparent 72%)',
            '--pn-mining-map-empty-bg': isDark ? 'rgba(11,13,26,0.88)' : 'rgba(255,255,255,0.88)',
          },
          body: {
            background: t.bg,
            color: t.textPrimary,
            fontFeatureSettings: '"cv11", "ss01", "ss03", "tnum"',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '::selection': {
            backgroundColor: isDark ? 'rgba(240,184,32,0.28)' : 'rgba(240,184,32,0.18)',
            color: t.textPrimary,
          },
          '*': {
            scrollbarColor: `${isDark ? 'rgba(148,163,184,0.28)' : 'rgba(100,116,139,0.30)'} transparent`,
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(148,163,184,0.24)' : 'rgba(100,116,139,0.26)',
            borderRadius: radii.rPill,
            border: `2px solid ${t.bg}`,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: isDark ? 'rgba(148,163,184,0.36)' : 'rgba(71,85,105,0.34)',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: radii.rPill,
            minHeight: 40,
            fontWeight: 510,
            paddingInline: space.s4,
            boxShadow: 'none',
            transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms',
            '&:active': { transform: 'translateY(1px)' },
          },
          containedPrimary: {
            background: b.goldFill,
            color: '#0F0A00',
            '&:hover': { background: b.goldFillHover },
            '&:active': { background: b.goldFillPressed },
          },
          outlined: {
            borderColor: t.borderStd,
            color: t.textPrimary,
            '&:hover': { borderColor: t.borderStrong, background: t.cardHover },
          },
          text: {
            color: t.textSecondary as string,
            '&:hover': { background: t.cardHover, color: t.textPrimary },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radii.r3,
            backgroundImage: 'none',
            backgroundColor: t.card,
            border: `1px solid ${t.borderSubtle}`,
            boxShadow: isDark ? 'none' : t.shadowElev1,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radii.r3,
            backgroundColor: t.card,
            border: `1px solid ${t.borderSubtle}`,
            boxShadow: isDark ? 'none' : t.shadowElev1,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radii.r4,
            backgroundColor: t.panel,
            border: `1px solid ${t.borderStd}`,
            boxShadow: t.shadowElev2,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radii.r2,
            backgroundColor: t.input,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.borderStd },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.borderStrong },
            '&.Mui-focused': {
              backgroundColor: t.inputFocus,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? b.goldFill : t.accentBorder,
                boxShadow: t.glowBrand,
              },
            },
          },
          input: {
            color: t.textPrimary,
            '&::placeholder': { color: t.textTertiary, opacity: 1 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: t.textTertiary,
            '&.Mui-focused': { color: isDark ? b.gold : t.textSecondary },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radii.rPill,
            fontSize: 11,
            fontWeight: 510,
            letterSpacing: '0.8px',
            fontFamily: fonts.mono,
            textTransform: 'uppercase',
            height: 22,
            border: `1px solid ${t.borderSubtle}`,
            backgroundColor: t.neutral,
          },
          colorPrimary: {
            backgroundColor: isDark ? t.brandSurface : t.surfaceMuted,
            color: isDark ? b.goldLight : t.accentText,
            borderColor: t.accentBorder,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: b.goldFill, height: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 510,
            color: t.textTertiary,
            minHeight: 40,
            '&.Mui-selected': { color: t.textPrimary },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: radii.r2,
            backgroundColor: t.panel,
            color: t.textPrimary,
            border: `1px solid ${t.borderStd}`,
            fontSize: 12,
            fontWeight: 510,
            boxShadow: t.shadowElev2,
          },
          arrow: { color: t.panel },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: t.divider },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            padding: 8,
          },
          switchBase: {
            '&.Mui-checked': {
              color: b.goldFill,
              '& + .MuiSwitch-track': { backgroundColor: b.goldFill, opacity: isDark ? 0.5 : 0.34 },
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: t.neutral, borderRadius: radii.rPill, height: 4 },
          bar: { backgroundColor: isDark ? b.goldFill : t.success, borderRadius: radii.rPill },
        },
      },
    },
    operator: {
      ...t,
      brand: b as typeof brand,
      space,
      radii,
      fonts,
      type,
      isDark,
    } as Theme['operator'],
  });
};

export type { Theme } from '@mui/material/styles';
